/**
 * SWW Entries — Line Item Property Injector + Widget Formatter
 * ─────────────────────────────────────────────────────────────────────────────
 * Reads variant entry data set by snippets/sww-entry-data.liquid and injects
 * hidden properties[...] inputs into add-to-cart forms just before submission.
 * Shopify stores these as line item properties shown in cart drawer, cart page,
 * and checkout automatically.
 *
 * Also exposes window.SWWEntries.getForVariant() so quick-entries.liquid and
 * cart-thermometer.js can inject properties into their own fetch calls.
 *
 * Also formats the sww-entries-widget numbers with thousand separators after
 * every server-side re-render.
 *
 * Rules:
 *   - 0 truck entries → property NOT injected → nothing shown in cart
 *   - 0 live entries  → property NOT injected → nothing shown in cart
 *   - Truck displayed = base × global_multiplier × badge_multiplier (merch only)
 *   - Truck displayed = base × badge_multiplier only  (free products: sww-no-multiply tag)
 *   - Live displayed  = base (NEVER multiplied — no global, no badge)
 *   - Quantity is handled by the widget Liquid snippet (prop_value × qty)
 */

(function () {
  'use strict';

  // ── Entry calculation ──────────────────────────────────────────────────────

  /**
   * Get final (multiplied) entries for a variant.
   *
   * Multiplier rules:
   *   Live entries      — NEVER multiplied (no global, no badge)
   *   Free products     — no global multiplier on truck (data-no-multiply="true")
   *                       badge multiplier still applies as a loyalty reward
   *   Everything else   — truck = base × globalMultiplier × badgeMultiplier
   *
   * Badge multiplier applies ONLY to truck entries, and ONLY for products
   * tagged 'sww-merch' (data-merch="true" on the wrapper).
   *
   * Primary source: DOM <sww-entry-data> elements (data attributes).
   *   These are rendered by snippets/sww-entry-data.liquid as invisible DOM
   *   nodes — they survive innerHTML injection (Dawn quick-add modals, section
   *   re-renders) where inline <script> tags do NOT execute.
   *
   * Fallback: window.SWWVariants / window.SWWMultiplier (legacy inline script
   *   path — still works on full product pages where scripts execute normally).
   *   Note: badge multiplier is NOT applied in the fallback path since merch
   *   status is not available there.
   *
   * @param  {string|number} variantId
   * @returns {{ truck: number, live: number }}
   */
  function getForVariant(variantId) {
    var vid = String(variantId);

    // ── Primary: DOM data-attribute lookup ──────────────────────────────────
    var spanEl = document.querySelector('sww-entry-data span[data-vid="' + vid + '"]');
    if (spanEl) {
      var wrapEl     = spanEl.closest('sww-entry-data');
      var globalMult = wrapEl ? (parseFloat(wrapEl.dataset.multiplier) || 1) : 1;
      var isMerch    = wrapEl && wrapEl.dataset.merch === 'true';
      var noMultiply = wrapEl && wrapEl.dataset.noMultiply === 'true'; // free products tag
      var badgeMult  = isMerch ? (parseFloat(window.SWWBadgeMultiplier) || 1) : 1;
      var truck      = parseInt(spanEl.dataset.truck, 10) || 0;
      var live       = parseInt(spanEl.dataset.live,  10) || 0;

      // Free products: skip global multiplier on truck, badge still applies
      var truckMult  = noMultiply ? badgeMult : (globalMult * badgeMult);

      return {
        truck: Math.round(truck * truckMult),
        live:  live,  // Live entries are NEVER multiplied
      };
    }

    // ── Fallback: window.SWWVariants (set by legacy inline-script path) ─────
    var variants   = window.SWWVariants   || {};
    var globalMult = parseFloat(window.SWWMultiplier) || 1;
    var v          = variants[vid];
    if (!v) return { truck: 0, live: 0 };
    return {
      truck: Math.round(v.truck * globalMult),
      live:  v.live,  // Live entries are NEVER multiplied
    };
  }

  // Expose publicly so quick-entries.liquid and cart-thermometer.js can use it
  window.SWWEntries = { getForVariant: getForVariant };

  // ── Property injection ─────────────────────────────────────────────────────

  // Read giveaway status from the first <sww-entry-data> element on the page.
  // This is a shop-level value — all instances carry the same values.
  function getGiveawayStatus() {
    var el = document.querySelector('sww-entry-data');
    return {
      truck: el ? el.dataset.truckGiveaway : 'inactive',
      live:  el ? el.dataset.liveGiveaway  : 'inactive',
    };
  }

  function injectEntryProperties(form) {
    // Remove any previously injected SWW inputs to avoid duplicates
    form.querySelectorAll('[data-sww-entry]').forEach(function (el) { el.remove(); });

    var variantInput = form.querySelector('input[name="id"]');
    if (!variantInput || !variantInput.value) return;

    var vid       = String(variantInput.value);
    var spanEl    = document.querySelector('sww-entry-data span[data-vid="' + vid + '"]');
    var wrapEl    = spanEl ? spanEl.closest('sww-entry-data') : null;
    var isMerch   = wrapEl && wrapEl.dataset.merch === 'true';
    var badgeMult = isMerch ? (parseFloat(window.SWWBadgeMultiplier) || 1) : 1;

    var entries  = getForVariant(variantInput.value);
    var giveaway = getGiveawayStatus();

    if (entries.truck > 0 && giveaway.truck === 'active') {
      var truckInp = document.createElement('input');
      truckInp.type  = 'hidden';
      truckInp.name  = 'properties[\uD83D\uDE9B Truck Entries]';
      var truckVal = entries.truck.toLocaleString();
      if (isMerch && badgeMult > 1) {
        var multStr = (badgeMult % 1 === 0 ? Math.round(badgeMult) : badgeMult) + 'x';
        truckVal += ' Entries (' + multStr + ')';
      }
      truckInp.value = truckVal;
      truckInp.setAttribute('data-sww-entry', 'truck');
      form.appendChild(truckInp);
    }


    // 📺 Live Entries — only when live giveaway is active
    if (entries.live > 0 && giveaway.live === 'active') {
      var liveInp = document.createElement('input');
      liveInp.type  = 'hidden';
      liveInp.name  = 'properties[\uD83D\uDCFA Live Entries]';
      liveInp.value = entries.live.toLocaleString();
      liveInp.setAttribute('data-sww-entry', 'live');
      form.appendChild(liveInp);
    }
  }

  // ── Delegated form listener ────────────────────────────────────────────────
  // Single capture-phase listener on document catches ALL add-to-cart forms —
  // product page, featured product, quick-add modals, collection page modals —
  // regardless of when they were dynamically added to the DOM.
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (form && form.getAttribute('data-type') === 'add-to-cart-form') {
      injectEntryProperties(form);
    }
  }, true);

  // ── Cart line-item entry quantity formatter ───────────────────────────────
  // cart-drawer.liquid and main-cart-items.liquid output entry totals as raw
  // integers inside <span class="sww-entry-qty" data-raw="3000">3000</span>.
  // This replaces the text with a locale-formatted string (3000 → 3,000).

  function formatEntryQty() {
    document.querySelectorAll('.sww-entry-qty[data-raw]').forEach(function (el) {
      var n = parseInt(el.dataset.raw, 10);
      if (!isNaN(n)) el.textContent = n.toLocaleString();
    });
  }

  // ── Widget number formatter ────────────────────────────────────────────────
  // The widget is server-rendered by sww-entries-widget.liquid with raw numbers.
  // This formats them with locale-aware thousand separators (e.g. 3100 → 3,100).

  function formatWidgets() {
    document.querySelectorAll('.sww-entries-widget').forEach(function (widget) {
      var total = parseInt(widget.getAttribute('data-total'), 10) || 0;
      var truck = parseInt(widget.getAttribute('data-truck'), 10) || 0;
      var live  = parseInt(widget.getAttribute('data-live'),  10) || 0;

      var totalEl = widget.querySelector('.sww-ew-total');
      if (totalEl) totalEl.textContent = total.toLocaleString();

      var truckEl = widget.querySelector('.sww-ew-truck');
      if (truckEl) truckEl.textContent = 'Truck Entries: ' + truck.toLocaleString();

      var liveEl = widget.querySelector('.sww-ew-live');
      if (liveEl) liveEl.textContent = 'Live Entries: ' + live.toLocaleString();
    });
  }

  // ── Product page entry badge updater ──────────────────────────────────────
  // When the customer selects a different variant, update the truck/live
  // entry badges overlaid on the product image with the new variant's values.
  // Reads BASE values (data-truck, data-live) from sww-entry-data spans and
  // the global multiplier from the wrapper — matching the server-side format:
  //   "X Truck Entries | 2x"  (X = base count, 2x = global multiplier)

  function updateProductPageBadges(variantId) {
    var vid    = String(variantId);
    var spanEl = document.querySelector('sww-entry-data span[data-vid="' + vid + '"]');
    var wrapEl = spanEl ? spanEl.closest('sww-entry-data') : document.querySelector('sww-entry-data');

    var truck        = spanEl ? (parseInt(spanEl.dataset.truck, 10) || 0) : 0;
    var live         = spanEl ? (parseInt(spanEl.dataset.live,  10) || 0) : 0;
    var mult         = wrapEl ? (parseFloat(wrapEl.dataset.multiplier) || 1) : 1;
    var truckGiveaway = wrapEl ? (wrapEl.dataset.truckGiveaway || 'inactive') : 'inactive';
    var liveGiveaway  = wrapEl ? (wrapEl.dataset.liveGiveaway  || 'inactive') : 'inactive';

    // Format multiplier — show as integer when whole number (1x, 2x not 1.5x)
    var multStr = (mult % 1 === 0 ? Math.round(mult) : mult) + 'x';

    var truckBadge = document.querySelector('.sww-entry-badge--truck');
    var liveBadge  = document.querySelector('.sww-entry-badge--live');

    if (truckBadge) {
      truckBadge.textContent = truck + ' Truck Entries | ' + multStr;
      truckBadge.style.display = (truck > 0 && truckGiveaway === 'active') ? '' : 'none';
    }
    if (liveBadge) {
      // Live entries are NEVER multiplied — show base count only, no multiplier suffix
      liveBadge.textContent = live + ' Live Entries';
      liveBadge.style.display = (live > 0 && liveGiveaway === 'active') ? '' : 'none';
    }
  }

  // ── Init ───────────────────────────────────────────────────────────────────

  function init() {
    // Format on initial page load
    formatWidgets();
    formatEntryQty();

    // Re-format after any cart update — Dawn re-renders sections which replaces
    // widget and cart-item HTML with fresh server-rendered raw numbers.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        // Small delay to let Dawn finish its DOM swap before we format
        setTimeout(function () {
          formatWidgets();
          formatEntryQty();
        }, 400);
      });
    }

    // Also observe DOM mutations to catch re-renders from thermometer,
    // quick-entries, and cart drawer / cart page section refreshes.
    var observer = new MutationObserver(function (mutations) {
      var shouldFormat = false;
      mutations.forEach(function (mutation) {
        mutation.addedNodes.forEach(function (node) {
          if (node.nodeType !== 1) return;
          if (
            (node.classList && node.classList.contains('sww-entries-widget')) ||
            (node.classList && node.classList.contains('sww-entry-qty')) ||
            (node.querySelector && node.querySelector('.sww-entries-widget, .sww-entry-qty'))
          ) {
            shouldFormat = true;
          }
        });
      });
      if (shouldFormat) {
        formatWidgets();
        formatEntryQty();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    // ── Product page variant change → update entry badges ──────────────────
    // Dawn's variant-selects and variant-radios fire `change` events on their
    // child inputs when a new variant is selected. We give Dawn a short tick
    // to update the hidden variant input before we read the new value.
    document.addEventListener('change', function (e) {
      if (!e.target) return;
      if (!e.target.closest('variant-selects, variant-radios')) return;
      // Only run if there are product page badges in the DOM
      if (!document.querySelector('.sww-entry-badges--product-page')) return;
      setTimeout(function () {
        var hiddenInput = (
          document.querySelector('product-form input[name="id"]') ||
          document.querySelector('input.product-variant-id') ||
          document.querySelector('form[action="/cart/add"] input[name="id"]')
        );
        if (hiddenInput && hiddenInput.value) {
          updateProductPageBadges(hiddenInput.value);
        }
      }, 50);
    }, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
